/**
 * 模块: CompetitorTracker数据管理Hook
 * 职责: 管理竞品数据加载、过滤和状态
 * 限制: 文件<280行, 函数<30行
 */

import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import api from '../../services/api';
import type { SKU, CompetitorASIN } from '../../types';

export const useCompetitorData = (id: string | undefined) => {
  const [loading, setLoading] = useState(false);
  const [sku, setSku] = useState<SKU | null>(null);
  const [competitors, setCompetitors] = useState<CompetitorASIN[]>([]);
  const [filteredCompetitors, setFilteredCompetitors] = useState<CompetitorASIN[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [threatFilter, setThreatFilter] = useState<string>('all');

  const loadData = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const [skuRes, competitorsRes] = await Promise.all([
        api.get(`/skus/${id}/`),
        api.get(`/skus/${id}/competitors/`),
      ]);

      setSku(skuRes.data);
      setCompetitors(competitorsRes.data);
    } catch (error) {
      message.error('加载数据失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const filterCompetitors = useCallback(() => {
    let filtered = [...competitors];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.asin.toLowerCase().includes(term) ||
          c.brand?.toLowerCase().includes(term)
      );
    }

    if (threatFilter !== 'all') {
      filtered = filtered.filter((c) => c.threat_level === threatFilter);
    }

    setFilteredCompetitors(filtered);
  }, [competitors, searchTerm, threatFilter]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id, loadData]);

  useEffect(() => {
    filterCompetitors();
  }, [competitors, searchTerm, threatFilter, filterCompetitors]);

  const threatStats = {
    high: competitors.filter((c) => c.threat_level === 'high').length,
    medium: competitors.filter((c) => c.threat_level === 'medium').length,
    low: competitors.filter((c) => c.threat_level === 'low').length,
  };

  return {
    loading,
    sku,
    competitors,
    filteredCompetitors,
    searchTerm,
    setSearchTerm,
    threatFilter,
    setThreatFilter,
    threatStats,
    loadData,
  };
};

